import { PostImage } from "./PostImage";
import { useState } from "react";
import { useQuery } from "urql";
import {
  Button,
  Card,
  ErrorState,
  LoadingState,
  PageHeader,
  Tag,
  PostsDocument,
  PostDocument,
  formatJstDateTime,
  type PostsQuery,
  type SourceType,
} from "visumo-data-agent-web-shared";
import { HelpIcon } from "../help/HelpIcon";
const LABELS: Record<SourceType, string> = {
  OFFICIAL_SNS: "公式SNS",
  UGC: "利用者の投稿",
  REVIEW: "レビュー",
  OTHER: "その他",
};
type PostRow = PostsQuery["posts"]["edges"][number]["node"];
function PostDetail({ id }: { id: string }) {
  const [result, refetch] = useQuery({
    query: PostDocument,
    variables: { id },
  });
  if (result.error)
    return (
      <ErrorState
        title="投稿を読み込めませんでした"
        description={result.error.message}
        onRetry={() => refetch({ requestPolicy: "network-only" })}
      />
    );
  if (!result.data)
    return <LoadingState label="投稿の詳細を読み込んでいます" />;
  const post = result.data.post;
  if (!post) return <p>投稿が見つからないか、閲覧できません。</p>;
  return (
    <div aria-label="投稿の詳細">
      <p>{post.bodyWithheld ? "本文を読む権限がありません" : post.title}</p>
      {!post.bodyWithheld && (
        <p style={{ whiteSpace: "pre-wrap" }}>
          {post.body ?? "本文が取り込まれていません"}
        </p>
      )}
      <p>動画 {post.videoCount}本</p>
    </div>
  );
}
export function PostsPage() {
  const [source, setSource] = useState<SourceType | "">("");
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  const [after, setAfter] = useState<string | null>(null);
  const [previous, setPrevious] = useState<PostRow[]>([]);
  const [opened, setOpened] = useState<string | null>(null);
  const [result, refetch] = useQuery({
    query: PostsDocument,
    variables: {
      first: 20,
      after,
      sourceType: source || null,
      query: query || null,
    },
  });
  const connection = result.data?.posts;
  const rows = [
    ...previous,
    ...(connection?.edges.map((edge) => edge.node) ?? []),
  ].filter(
    (row, index, all) =>
      all.findIndex((other) => other.id === row.id) === index,
  );
  const canSearch = rows.some((row) => !row.bodyWithheld) || query.length > 0;
  function reset() {
    setAfter(null);
    setPrevious([]);
    setOpened(null);
  }
  return (
    <div className="vd-stack">
      <PageHeader
        title="投稿"
        description="取り込まれた投稿を、新しい順に確認できます。"
        titleExtra={<HelpIcon slug="products/posts" />}
      />
      <p className="vd-stat-note">
        公式SNSとレビューを区別する情報は未接続です。利用者の投稿と確認できるもの以外は「その他」と表示します。
      </p>
      <form
        className="vd-inline"
        onSubmit={(event) => {
          event.preventDefault();
          setQuery(input.trim());
          reset();
        }}
      >
        <label>
          種別
          <select
            className="vd-select"
            value={source}
            onChange={(event) => {
              setSource(event.target.value as SourceType | "");
              reset();
            }}
          >
            <option value="">すべて</option>
            {Object.entries(LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        {canSearch && (
          <>
            <label>
              本文を検索
              <input
                className="vd-input"
                value={input}
                maxLength={200}
                onChange={(event) => setInput(event.target.value)}
              />
            </label>
            <Button type="submit" disabled={result.fetching}>
              検索する
            </Button>
          </>
        )}
      </form>
      {result.error ? (
        <ErrorState
          title="投稿を読み込めませんでした"
          description={result.error.message}
          onRetry={() => refetch({ requestPolicy: "network-only" })}
        />
      ) : !connection ? (
        <LoadingState label="投稿を読み込んでいます" />
      ) : connection.dataGap ? (
        <Card title="投稿を表示できません">
          <p>{connection.dataGap.why}</p>
        </Card>
      ) : (
        <>
          {connection.totalCount != null && <p>{connection.totalCount}件</p>}
          {rows.length === 0 ? (
            <Card
              title={
                source || query
                  ? "条件に合う投稿はありません"
                  : "投稿はまだ取り込まれていません"
              }
            >
              {null}
            </Card>
          ) : (
            rows.map((post) => (
              <Card
                key={post.id}
                title={post.bodyWithheld ? "投稿" : (post.title ?? "投稿")}
                extra={<Tag>{LABELS[post.sourceType]}</Tag>}
              >
                <div data-testid="post-row">
                  <PostImage key={post.mediaUrl} url={post.mediaUrl ?? null} />
                  <p>
                    {post.bodyWithheld
                      ? "本文を読む権限がありません"
                      : (post.body?.slice(0, 120) ??
                        "本文が取り込まれていません")}
                  </p>
                  <p>
                    {post.postedAt
                      ? formatJstDateTime(post.postedAt)
                      : "投稿日が取り込まれていません"}{" "}
                    · {post.isDisplayed ? "表示中" : "非表示"}
                  </p>
                  <p>
                    タグ {post.tagCount}件 · 商品との紐づけ{" "}
                    {post.productLinkCount}件 · いいね{" "}
                    {post.reactions ?? "未取得"}
                  </p>
                  <Button
                    aria-expanded={opened === post.id}
                    onClick={() =>
                      setOpened(opened === post.id ? null : post.id)
                    }
                  >
                    {opened === post.id ? "詳細を閉じる" : "詳細を開く"}
                  </Button>
                  {opened === post.id && <PostDetail id={post.id} />}
                </div>
              </Card>
            ))
          )}
          {connection.pageInfo.hasNextPage && (
            <Button
              disabled={result.fetching}
              onClick={() => {
                setPrevious(rows);
                setAfter(connection.pageInfo.endCursor ?? null);
              }}
            >
              続きを読む
            </Button>
          )}
        </>
      )}
    </div>
  );
}
